import { Component, OnInit } from "@angular/core";
import {
  Company,
  CompanyInfo,
  GroupedPermissions,
  Product,
  Role,
} from "../interfaces/companies";
import { ProductService } from "../services/product.service";
import { UserManagementService } from "../services/user-management.service";

@Component({
  standalone: false,
  selector: "app-company-management",
  templateUrl: "./company-management.component.html",
  styleUrls: ["./company-management.component.scss"],
})
export class CompanyManagementComponent implements OnInit {
  productDialog = false;
  companyDialog = false;
  deleteCompanyDialog = false;
  deleteRoleDialog = false;
  roleDialog = false;

  company: Object;
  newCompany: boolean;
  editCompany: CompanyInfo;
  newRole: boolean;
  editRole: Role;
  product: Product;
  availablePermissionsForProduct: string[];
  suggestedCompanies: Company[] = [];
  groupedPermissions: GroupedPermissions[];

  companyButtonItems: any[];
  roleButtonItems: any[];
  targetCompany: CompanyInfo;
  targetRole: Role;
  invalidCompanyName: boolean;
  invalidCompanyCode: boolean;

  constructor(
    public userManagementService: UserManagementService,
    public productService: ProductService
  ) {
    this.companyButtonItems = [
      {
        label: "Delete",
        icon: "fas fa-trash-alt primary-color",
        command: () => {
          this.onDeleteCompany();
        },
      },
    ];

    this.roleButtonItems = [
      {
        label: "Delete",
        icon: "fas fa-trash-alt primary-color",
        command: () => {
          this.onDeleteRole();
        },
      },
    ];
  }

  ngOnInit(): void {
    this.userManagementService.init();
  }

  setTargetCompany(company: CompanyInfo) {
    this.targetCompany = company;
  }
  setTargetRole(role: Role) {
    this.targetRole = role;
  }
  onCompanyNameInput(event: any) {
    const inputValue = event.target.value;
    //Allowed characters: a-Z, 0-9, space, -, _. Must begin and end with letter or number. Can not be empty.
    /** Regex break down
     * !/^[0-9\p{L}\p{M}]+ -> Must Begin '^' with one or more '+' of '[0-9\p{L}\p{M}]' where \p{L} are unicode letters (includes latin) and \p{M} are unicode marks. https://www.regular-expressions.info/unicode.html#prop https://stackoverflow.com/questions/280712/how-can-i-use-unicode-aware-regular-expressions-in-javascript
     * ([ \-_]*[0-9\p{L}\p{M}]+)*$ -> Allow 0 or more groups '()*' of
     *    [ \-_]*[0-9\p{L}\p{M}]+ -> 0 or more of spaces, dashes or underscores followed by  1 or more of '[0-9\p{L}\p{M}]'
     * Basically it says this string must start with AlphaNumeric characters and can then optionally include dashes, underscores or spaces which must be followed by more AlphaNumeric characters.
     * */
    this.invalidCompanyName =
      !/^[0-9\p{L}\p{M}]+([- _]*[0-9\p{L}\p{M}]+)*$/gu.test(inputValue);
  }

  onCompanyCodeInput(event: any) {
    const inputValue = event.target.value;
    //Allowed characters: a-Z, 0-9. Can not be empty.
    this.invalidCompanyCode = !/^[A-Za-z0-9]+$/g.test(inputValue);
  }

  searchCompanies(event) {
    const regex = new RegExp(".*" + event.query + ".*", "i"); // Case Insensitive Regex substring query. Same as .contains() but insensitive
    this.suggestedCompanies = this.userManagementService.companies.filter(
      (company) => regex.test(company.name)
    );
  }

  addCompany() {
    this.editCompany = {
      name: "",
      code: "",
      products: [],
    };
    this.newCompany = true;
    this.companyDialog = true;
  }

  saveCompany() {
    this.editCompany.products = this.editCompany.products.filter(
      (product) => product
    ); // Filter out possible nulls
    if (this.newCompany) {
      this.userManagementService.addNewCompany(this.editCompany);
    } else {
      this.userManagementService.setCompanyProducts(this.editCompany);
    }
    this.hideDialog();
  }

  deleteCompany() {
    this.userManagementService.deleteCompany(this.targetCompany);
    this.hideDialog();
  }

  onDeleteCompany() {
    this.deleteCompanyDialog = true;
  }

  deleteRole() {
    this.userManagementService.deleteRole(this.targetRole);
    this.hideDialog();
  }

  onDeleteRole() {
    this.deleteRoleDialog = true;
  }

  openUpdateCompanyDialog(company) {
    this.companyDialog = true;
    this.newCompany = false;
    this.editCompany = {
      name: company.name,
      code: company.code,
      products: company.products,
    };
  }

  openUpdateRoleDialog(company, role) {
    this.groupedPermissions =
      this.userManagementService.getPermissionsForCompany(company.code);
    this.roleDialog = true;
    this.newRole = false;
    this.editRole = {
      name: role.name,
      company: {
        code: company.code,
        name: company.name,
      },
      permissions: role.permissions,
      products: role.products,
    };
  }

  openRoleCreationDialog(company: CompanyInfo) {
    this.groupedPermissions =
      this.userManagementService.getPermissionsForCompany(company.code);
    this.newRole = true;
    this.roleDialog = true;
    this.editRole = {
      name: "",
      company: {
        code: company.code,
        name: company.name,
      },
      permissions: [],
      products: [],
    };
  }

  saveRole() {
    const products = [
      ...new Set(this.editRole.permissions.map((name) => name.split(":")[0])),
    ];
    this.editRole.products = products;
    if (this.newRole) {
      this.userManagementService.addNewRole(this.editRole);
    } else {
      this.userManagementService.updateRole(this.editRole);
    }

    this.hideDialog();
  }

  hideDialog() {
    this.companyDialog = false;
    this.roleDialog = false;
    this.deleteCompanyDialog = false;
    this.deleteRoleDialog = false;
    this.invalidCompanyCode = false;
    this.invalidCompanyName = false;
  }
}
